<template>
  <focus-trap :clickDeactivates="false" :escapeDeactivates="false">
    <div
      class="examination-pdf-file-popup"
      :tabindex="`-${layerNumber}`"
      @keyup.esc.stop="handleClose"
    >
      <validation-observer v-slot="{ invalid }">
        <popup
          :buttons="['閉じる', '登録']"
          :content-style="{}"
          :invalid="isInvalid(invalid)"
          :layer-number="layerNumber"
          @cancel="handleClose"
          @click-close-mark="handleClose"
          @decision="handleDecide"
        >
          <template v-slot:content>
            <div class="content">
              <div class="header">
                <div class="title">{{ titleText }}</div>
              </div>
              <div class="input-form">
                <div class="row">
                  <date-input-form
                    data-test="date-input-form"
                    :date="examinationPdfFile.date"
                    :headingStyle="{ width: '200px', minWidth: '200px' }"
                    @input-date="inputValue('date', $event)"
                    >検査日</date-input-form
                  >
                </div>
                <div class="row" examination>
                  <text-input-form
                    data-test="text-input-form examination-name"
                    :headingStyle="{ width: '200px', minWidth: '200px' }"
                    :maxlength="50"
                    :placeholder="'50文字'"
                    :requiredFlg="true"
                    :styles="{ width: '400px' }"
                    v-model="examinationPdfFile.examinationName"
                    >検査名</text-input-form
                  >
                </div>
                <div class="row">
                  <select-box-form
                    data-test="select-box-form"
                    :selectData="[
                      { id: 1, name: '〇' },
                      { id: 0, name: '✕' }
                    ]"
                    :styles="{ width: '60px' }"
                    v-model="examinationPdfFile.bloodFlg"
                    >血液検査</select-box-form
                  >
                </div>
                <div class="row file">
                  <div class="label">
                    <div class="text">{{ 'ファイル' }}</div>
                    <div class="required">必須</div>
                  </div>
                  <div class="field">
                    <base-file-input
                      data-test="base-file-input"
                      :accept="'.pdf'"
                      :disabled="isFileChanging"
                      :input-style="{ height: '50px' }"
                      :label="
                        'PDFファイルを選択 もしくは ドラッグ＆ドロップしてください'
                      "
                      @input="inputFile"
                    />
                    <div
                      v-if="examinationPdfFile.encodedTextFile"
                      class="file-detail"
                      data-test="file-detail"
                    >
                      <div class="file-name">
                        <div class="icon">
                          <img
                            class="check"
                            alt="ファイル読み込み完了アイコン"
                            src="@/assets/images/check_16px.png"
                            width="16px"
                            height="16px"
                          />
                        </div>
                        <div class="text">
                          <span :title="examinationPdfFile.name">
                            {{ examinationPdfFile.name }}
                          </span>
                        </div>
                      </div>
                      <base-button-border-orange
                        data-test="base-button-border-orange"
                        :styles="{ width: '100px' }"
                        @click="openPdf"
                        >PDF確認</base-button-border-orange
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </popup>
      </validation-observer>
      <announce-popup
        v-if="alertPopup.isOpened"
        data-test="announce-popup alert"
        :buttons="alertPopup.buttons"
        :layerNumber="alertPopup.layerNumber"
        :title="alertPopup.title"
        :type="alertPopup.type"
        @close="closeAlertPopup"
        >{{ alertPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="confirmationPopup.isOpened"
        data-test="announce-popup confirmation"
        :buttons="confirmationPopup.buttons"
        :layerNumber="confirmationPopup.layerNumber"
        :title="confirmationPopup.title"
        :type="confirmationPopup.type"
        @cancel="closeConfirmationPopup"
        @decision="closeSelf"
        >{{ confirmationPopup.message }}</announce-popup
      >
      <announce-popup
        v-if="resultPopup.isOpened"
        data-test="announce-popup result"
        :buttons="resultPopup.buttons"
        :layerNumber="resultPopup.layerNumber"
        :title="resultPopup.title"
        :type="resultPopup.type"
        @close="closeResultPopup"
        >{{ resultPopup.message }}</announce-popup
      >
    </div>
  </focus-trap>
</template>

<script>
import AnnouncePopup from '@/components/popups/AnnouncePopup'
import BaseButtonBorderOrange from '@/components/parts/atoms/BaseButtonBorderOrange'
import BaseFileInput from '@/components/parts/atoms/BaseFileInput'
import CheckPopupInputDifference from '@/components/mixins/CheckPopupInputDifference'
import DateInputForm from '@/components/parts/molecules/DateInputForm'
import Popup from '@/components/popups/Popup'
import SelectBoxForm from '@/components/parts/molecules/SelectBoxForm'
import TextInputForm from '@/components/parts/molecules/TextInputForm'
import _ from 'lodash'
import moment from 'moment'
import { FocusTrap } from 'focus-trap-vue'
import { PDFDocument } from 'pdf-lib'
import { ValidationObserver } from 'vee-validate'
import { mapGetters } from 'vuex'

export default {
  name: 'ExaminationPdfFilePopup',

  components: {
    AnnouncePopup,
    BaseButtonBorderOrange,
    BaseFileInput,
    DateInputForm,
    FocusTrap,
    Popup,
    SelectBoxForm,
    TextInputForm,
    ValidationObserver
  },

  mixins: [CheckPopupInputDifference],

  props: {
    encodedTextFile: { type: String, default: null },
    examinationPdfFileId: { type: Number, default: 0 },
    layerNumber: { type: Number, default: 1 },
    patientId: { type: Number, default: 0 }
  },

  data() {
    return {
      alertPopup: {
        buttons: ['閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '',
        title: '注意',
        type: 'alert'
      },
      awaiting: false,
      confirmationPopup: {
        buttons: ['キャンセル', '閉じる'],
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '入力内容を保存せずに閉じようとしています。\nよろしいですか？',
        title: '確認',
        type: 'alert'
      },
      examinationPdfFile: {
        patientId: this.patientId,
        date: moment().format('YYYYMMDD'),
        examinationName: '',
        bloodFlg: 0,
        encodedTextFile: null,
        name: ''
      },
      initialData: {},
      inputData: {},
      isFileChanging: false,
      resultPopup: {
        buttons: ['閉じる'],
        close: this.closeResultPopup,
        isOpened: false,
        layerNumber: this.layerNumber + 1,
        message: '',
        title: '',
        type: ''
      },
      shouldCloseSelf: false
    }
  },

  computed: {
    ...mapGetters({
      getExaminationPdfFile: 'examinationPdfFiles/getDataById',
      lookOnlyFlg: 'auth/lookOnlyFlg'
    }),
    diffFlg() {
      return !_.isEqual(this.initialData, this.examinationPdfFile)
    },
    editing() {
      return !!this.examinationPdfFileId
    },
    titleText() {
      return this.editing ? '検査結果PDF編集' : '検査結果PDF登録'
    }
  },

  created() {
    this.setData()
  },

  methods: {
    closeAlertPopup() {
      this.alertPopup.isOpened = false
    },
    closeConfirmationPopup() {
      this.confirmationPopup.isOpened = false
    },
    closeResultPopup() {
      this.resultPopup.isOpened = false
      if (this.shouldCloseSelf) {
        this.closeSelf()
      }
    },
    closeSelf() {
      this.$emit('close')
    },
    async decideCreate() {
      this.awaiting = true
      const result = await this.$store.dispatch('examinationPdfFiles/create', {
        examinationPdfFile: this.examinationPdfFile
      })
      if (result?.status === 'success') {
        this.initialData = { ...this.examinationPdfFile }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '登録しました',
          title: '完了',
          type: 'success'
        }
        this.updateFetchedFileMap(
          result.examinationPdfFileId,
          this.examinationPdfFile.encodedTextFile
        )
        this.shouldCloseSelf = true
      } else {
        let message = '登録に失敗しました'
        if (result === 'no patient') {
          message = '登録に失敗しました。\n対象の患者は既に削除されています。'
          this.shouldCloseSelf = true
        }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message,
          title: '失敗',
          type: 'failure'
        }
      }
      this.awaiting = false
    },
    async decideUpdate() {
      this.awaiting = true
      const result = await this.$store.dispatch('examinationPdfFiles/update', {
        examinationPdfFile: this.examinationPdfFile
      })
      if (result?.status === 'success') {
        this.initialData = { ...this.examinationPdfFile }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message: '編集しました',
          title: '完了',
          type: 'success'
        }
        this.updateFetchedFileMap(
          result.examinationPdfFileId,
          this.examinationPdfFile.encodedTextFile
        )
        this.shouldCloseSelf = true
      } else {
        let message = '編集に失敗しました'
        if (
          result === 'no data in clinic' ||
          result === 'no examinationPdfFile'
        ) {
          message =
            '編集に失敗しました。\n対象の検査結果PDFは既に削除されています。'
          this.shouldCloseSelf = true
        } else if (result === 'no patient') {
          message = '編集に失敗しました。\n対象の患者は既に削除されています。'
          this.shouldCloseSelf = true
        }
        this.resultPopup = {
          ...this.resultPopup,
          isOpened: true,
          message,
          title: '失敗',
          type: 'failure'
        }
      }
      this.awaiting = false
    },
    handleClose() {
      if (this.diffFlg) {
        this.confirmationPopup.isOpened = true
      } else {
        this.closeSelf()
      }
    },
    async handleDecide() {
      if (this.editing) {
        await this.decideUpdate()
      } else {
        await this.decideCreate()
      }
    },
    async inputFile(file) {
      if (!file || this.isFileChanging) return
      this.isFileChanging = true
      const openAlert = message => {
        this.alertPopup = { ...this.alertPopup, isOpened: true, message }
      }
      if (file.type !== 'application/pdf') {
        openAlert('PDFファイルを選択してください')
        this.isFileChanging = false
        return
      }
      if (1024 * 1024 < file.size) {
        openAlert('1MBを超えるファイルは登録できません')
        this.isFileChanging = false
        return
      }
      const encodedTextFile = await this.readAsBase64(file)
      if (encodedTextFile) {
        this.examinationPdfFile.encodedTextFile = encodedTextFile
        this.examinationPdfFile.name = file.name
      } else {
        openAlert('ファイルの読み込みに失敗しました')
      }
      this.isFileChanging = false
    },
    inputValue(key, value) {
      this.examinationPdfFile[key] = value
    },
    isInvalid(invalid) {
      return (
        !!this.lookOnlyFlg ||
        this.awaiting ||
        invalid ||
        !this.examinationPdfFile.encodedTextFile
      )
    },
    async openPdf() {
      const encodedTextFile = this.examinationPdfFile.encodedTextFile
      const pdfDoc = await PDFDocument.load(encodedTextFile)
      pdfDoc.setTitle(this.examinationPdfFile.name, {
        showInWindowTitleBar: true
      })
      const savedPdfBytes = await pdfDoc.save()
      const blob = new Blob([savedPdfBytes], { type: 'application/pdf' })
      const link = document.createElement('a')
      link.setAttribute('id', 'pdf-link')
      link.href = window.URL.createObjectURL(blob)
      window.open(link)
    },
    async readAsBase64(file) {
      const promise = new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          resolve(reader.result)
        }
        reader.onerror = () => {
          reject()
        }
      })
        .then(dataUrl => {
          return dataUrl
        })
        .catch(() => {
          return ''
        })
      const dataUrl = await promise
      const [, ...rest] = dataUrl.split(',')
      const base64String = rest.join('')
      return base64String
    },
    setData() {
      if (this.editing) {
        const stateRecord = this.getExaminationPdfFile(
          this.examinationPdfFileId
        )
        this.examinationPdfFile = {
          id: stateRecord.id,
          patientId: this.patientId,
          date: stateRecord.date,
          examinationName: stateRecord.examinationName,
          bloodFlg: stateRecord.bloodFlg,
          encodedTextFile: this.encodedTextFile,
          name: stateRecord.name
        }
      }
      this.initialData = { ...this.examinationPdfFile }
    },
    updateFetchedFileMap(examinationPdfFileId, encodedTextFile) {
      const latestExaminationPdfFile = this.getExaminationPdfFile(
        examinationPdfFileId
      )
      const updatedAt = latestExaminationPdfFile.updatedAt
      this.$emit('update', { encodedTextFile, examinationPdfFileId, updatedAt })
    }
  }
}
</script>

<style lang="scss" scoped>
.examination-pdf-file-popup {
  position: absolute;
  top: 0;
  left: 0;
  .content {
    min-width: 790px;
    min-height: 370px;
    > .header {
      display: flex;
      justify-content: space-between;
      height: 33px;
      > .title {
        text-align: left;
        font-size: 16px;
        font-weight: bold;
        color: #{$greyish-brown};
      }
    }
    > .input-form {
      margin-top: 25px;
      flex: 1;
      display: flex;
      flex-direction: column;
      row-gap: 25px;
      > .row {
        ::v-deep .select-box-form .heading {
          width: 200px;
        }
        ::v-deep .text-input-form .body .validate .error {
          z-index: 0; // date-picker の popover と被らないようにするため
        }
        &.file {
          display: flex;
          > .label {
            display: flex;
            align-items: center;
            column-gap: 10px;
            width: 200px;
            height: 33px;
            font-size: 15px;
            > .required {
              font-size: 13px;
              color: #{$tomato};
            }
          }
          > .field {
            display: flex;
            flex-direction: column;
            row-gap: 10px;
            width: 560px;
            font-size: 15px;
            > .file-detail {
              display: flex;
              flex-direction: column;
              row-gap: 10px;
              width: 100%;
              > .file-name {
                display: flex;
                column-gap: 4px;
                > .icon > .check {
                  padding-top: 4px;
                  width: 16px;
                  height: 16px;
                }
                > .text {
                  line-height: 23px;
                  overflow: hidden;
                  overflow-wrap: break-word;
                }
              }
            }
          }
        }
      }
    }
  }
}
</style>
